// This is a helper to get static content data from our backend.
import gql from "graphql-tag";

import {Persist, standaloneGqlFetch} from "@repo/data-gql";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {RequestInfo} from "@repo/shared-core";

import formatQueryString, {
    type QueryParameters,
} from "#shared-package/format-query-string.ts";

import type {ContentOptions} from "./types.ts";

type FetchContentDataResponse = {
    contentJson: string;
};
type GqlContentOperation = GqlOperation<
    FetchContentDataResponse,
    ContentVariables
>;

// Breaking this out so we can port one type at a time.
// The Generic version is the one that handles all routes
// But we want specific versions of this query for now
// so that we can port them over one at a time instead of
// a single, monsterous, change.
const GENERIC_CONTENT_QUERY: GqlContentOperation = gqlOp(gql`
    query FetchContentData(
        $path: String!
        $queryParams: String!
        $isModal: Boolean
        $followRedirects: Boolean
        $countryCode: String!
    ) {
        contentJson: curatedContentLibraryJson(
            path: $path
            queryParams: $queryParams
            isModal: $isModal
            followRedirects: $followRedirects
            countryCode: $countryCode
        )
    }
`);
export const QUERY_FOR_TESTING: GqlContentOperation = GENERIC_CONTENT_QUERY;

const ARTICLE_CONTENT_QUERY: GqlContentOperation = gqlOp(gql`
    query FetchArticleContentData(
        $path: String!
        $queryParams: String!
        $isModal: Boolean
        $followRedirects: Boolean
        $countryCode: String!
    ) {
        contentJson: curatedArticleContentLibraryJson(
            path: $path
            queryParams: $queryParams
            isModal: $isModal
            followRedirects: $followRedirects
            countryCode: $countryCode
        )
    }
`);

const VIDEO_CONTENT_QUERY: GqlContentOperation = gqlOp(gql`
    query FetchVideoContentData(
        $path: String!
        $queryParams: String!
        $isModal: Boolean
        $followRedirects: Boolean
        $countryCode: String!
    ) {
        contentJson: curatedVideoContentLibraryJson(
            path: $path
            queryParams: $queryParams
            isModal: $isModal
            followRedirects: $followRedirects
            countryCode: $countryCode
        )
    }
`);

const EXERCISE_CONTENT_QUERY: GqlContentOperation = gqlOp(gql`
    query FetchExerciseContentData(
        $path: String!
        $queryParams: String!
        $isModal: Boolean
        $followRedirects: Boolean
        $countryCode: String!
    ) {
        contentJson: curatedExerciseContentLibraryJson(
            path: $path
            queryParams: $queryParams
            isModal: $isModal
            followRedirects: $followRedirects
            countryCode: $countryCode
        )
    }
`);

const PROGRAMMING_CONTENT_QUERY: GqlContentOperation = gqlOp(gql`
    query FetchProgrammingContentData(
        $path: String!
        $queryParams: String!
        $isModal: Boolean
        $followRedirects: Boolean
        $countryCode: String!
    ) {
        contentJson: curatedProgrammingContentLibraryJson(
            path: $path
            queryParams: $queryParams
            isModal: $isModal
            followRedirects: $followRedirects
            countryCode: $countryCode
        )
    }
`);

export function getContentQuery(
    path: string,
    isModal: boolean,
): GqlOperation<FetchContentDataResponse, ContentVariables> {
    if (isModal) {
        // modal content should return the unit content,
        // not the node content
        return GENERIC_CONTENT_QUERY;
    }

    if (RegExp(".*/a/.*").test(path)) {
        return ARTICLE_CONTENT_QUERY;
    }

    if (RegExp(".*/v/.*").test(path)) {
        return VIDEO_CONTENT_QUERY;
    }

    if (RegExp(".*/e/.*").test(path)) {
        return EXERCISE_CONTENT_QUERY;
    }

    if (RegExp(".*/(?:pc|pi|pp|pt)/.*").test(path)) {
        return PROGRAMMING_CONTENT_QUERY;
    }

    return GENERIC_CONTENT_QUERY;
}

/**
 * Verifies if we should use the followRedirects variable.
 *
 * @param {QueryParameters} queryParams The query params passed in the URL.
 * @returns {boolean} The calculated value of followRedirects.
 */
function getFollowRedirects(queryParams: QueryParameters) {
    if (queryParams["follow_redirects"] === undefined) {
        // Always follow redirects if there is no query string set.
        return true;
    }

    // If there's a query string set, verify if it has the value of `1` or `0`.
    return queryParams["follow_redirects"] === "1";
}

/**
 * Fetch static content data from the server.
 *
 * Returns a promise with JSON data from the server.  This data is untyped
 * because it is somewhat dynamic, but handled by the callers with some more
 * typing once the data is received.
 *
 * @param {ContentOptions} options for the content, which includes the path of the
 * content and the query parameters of the content.  This comes from the URL
 * of the the request, and gets passed to GraphQL to get the cached data
 * @returns {Promise<any>}
 */
export default function fetchContentData(
    options: ContentOptions,
): Promise<any> {
    const path = options.path.startsWith("/")
        ? options.path.substring(1)
        : options.path;

    const {queryParams = {} as QueryParameters} = options;

    const formattedQueryParams = formatQueryString({
        ...options.queryParams,
        lang: RequestInfo.kaLocale,
    });

    const isModal = queryParams["modal"] === "1";
    const followRedirects = getFollowRedirects(queryParams);

    const variables = {
        path,
        queryParams: formattedQueryParams,
        isModal,
        followRedirects,
        countryCode: RequestInfo.remoteCountry,
    } as const;

    const query = getContentQuery(path, isModal);
    return fetchContent(query, variables);
}

type ContentVariables = {
    path: string;
    queryParams: string;
    isModal: boolean;
    followRedirects: boolean;
    countryCode: string;
};

async function fetchContent(
    query: GqlOperation<FetchContentDataResponse, ContentVariables>,
    variables: ContentVariables,
): Promise<any> {
    // TODO(somewhatabstract):
    // We really should get gqlFetch from the React tree, but that just isn't how
    // this works right now, so let's use our standalone approach.
    // eslint-disable-next-line no-restricted-syntax
    const data = await standaloneGqlFetch(query, {
        variables,
        context: {
            fastlyCaching: Persist.UntilPublish,
        },
    });
    return JSON.parse(data.contentJson ?? "");
}
